/**
 * Settings page
 * Configure application settings
 */

import { useState } from 'react';
import { Tabs } from 'antd';
import {
  SettingOutlined,
  CloudOutlined,
  BellOutlined,
  WarningOutlined,
  FilterOutlined,
  AppstoreOutlined,
  StopOutlined,
} from '@ant-design/icons';
import { PageHeader } from '@/components/common/PageHeader';
import {
  ProxySettings,
  ScraperSettings,
  NotificationSettings,
  DangerZone,
  SearchTermVisibility,
  CategoryVisibility,
  NameBlacklist,
} from '@/components/settings';
import { useSettings, useUpdateSettings } from '@/hooks/useSettings';
import { useProxies, useTestProxy, useTestAllProxies, useDeleteProxy, useAddProxy } from '@/hooks/useProxies';
import { stopAllJobs, resetSettings as resetSettingsApi, factoryReset, killChrome, getChromeCount, refreshProxies } from '@/services/systemApi';
import { useNotification } from '@/stores/useNotificationStore';

export function SettingsPage() {
  const [activeTab, setActiveTab] = useState('scraper');
  const [refreshingProxies, setRefreshingProxies] = useState(false);
  const { success, error } = useNotification();

  const { data: settings, isLoading: settingsLoading, refetch: refetchSettings } = useSettings();
  const updateSettings = useUpdateSettings();

  const { data: proxiesData, refetch: refetchProxies } = useProxies();
  const addProxy = useAddProxy();
  const deleteProxy = useDeleteProxy();
  const testProxy = useTestProxy();
  const testAllProxies = useTestAllProxies();

  const handleSaveScraperSettings = async (values: any) => {
    // Hook (useUpdateSettings) already shows notifications
    await updateSettings.mutateAsync({ scraper: values });
  };

  const handleSaveNotificationSettings = async (values: any) => {
    // Hook (useUpdateSettings) already shows notifications
    await updateSettings.mutateAsync({ notifications: values });
  };

  const handleAddProxy = async (proxy: { host: string; port: number; username?: string; password?: string; protocol?: 'http' | 'https' | 'socks5' }) => {
    // Hook already shows notifications
    await addProxy.mutateAsync(proxy);
    refetchProxies();
  };

  const handleDeleteProxy = async (proxyId: string) => {
    // Hook already shows notifications
    await deleteProxy.mutateAsync(proxyId);
    refetchProxies();
  };

  const handleTestProxy = async (proxyId: string) => {
    // Hook already shows toast notifications
    await testProxy.mutateAsync(proxyId);
    refetchProxies();
  };

  const handleTestAllProxies = async () => {
    // Hook already shows toast notifications
    await testAllProxies.mutateAsync();
    refetchProxies();
  };

  const handleRefreshProxies = async () => {
    setRefreshingProxies(true);
    try {
      const result = await refreshProxies();
      success('Proxies refreshed', result.message);
      refetchProxies();
    } catch (err) {
      error('Failed', 'Failed to refresh proxies from ProxyScrape');
    } finally {
      setRefreshingProxies(false);
    }
  };

  // Danger Zone handlers - return the API response directly for modal display
  const handleStopAllJobs = async () => {
    return await stopAllJobs();
  };

  const handleResetSettings = async () => {
    const result = await resetSettingsApi();
    refetchSettings();
    return result;
  };

  const handleFactoryReset = async () => {
    return await factoryReset();
  };

  const handleKillChrome = async () => {
    return await killChrome();
  };

  const handleGetChromeCount = async () => {
    return await getChromeCount();
  };

  const tabItems = [
    {
      key: 'scraper',
      label: (
        <span>
          <SettingOutlined style={{ marginRight: 8 }} />
          Scraper
        </span>
      ),
      children: (
        <ScraperSettings
          settings={settings?.scraper}
          loading={settingsLoading || updateSettings.isPending}
          onSave={handleSaveScraperSettings}
          onReset={handleResetSettings}
        />
      ),
    },
    {
      key: 'proxies',
      label: (
        <span>
          <CloudOutlined style={{ marginRight: 8 }} />
          Proxies
        </span>
      ),
      children: (
        <ProxySettings
          proxies={proxiesData?.items || []}
          loading={false}
          onAdd={handleAddProxy}
          onDelete={handleDeleteProxy}
          onTest={handleTestProxy}
          onTestAll={handleTestAllProxies}
          onRefresh={handleRefreshProxies}
          refreshing={refreshingProxies}
        />
      ),
    },
    {
      key: 'notifications',
      label: (
        <span>
          <BellOutlined style={{ marginRight: 8 }} />
          Notifications
        </span>
      ),
      children: (
        <NotificationSettings
          settings={settings?.notifications}
          loading={settingsLoading || updateSettings.isPending}
          onSave={handleSaveNotificationSettings}
        />
      ),
    },
    {
      key: 'search-terms',
      label: (
        <span>
          <FilterOutlined style={{ marginRight: 8 }} />
          Search Terms
        </span>
      ),
      children: <SearchTermVisibility />,
    },
    {
      key: 'categories',
      label: (
        <span>
          <AppstoreOutlined style={{ marginRight: 8 }} />
          Categories
        </span>
      ),
      children: <CategoryVisibility />,
    },
    {
      key: 'name-blacklist',
      label: (
        <span>
          <StopOutlined style={{ marginRight: 8 }} />
          Name Blacklist
        </span>
      ),
      children: <NameBlacklist />,
    },
    {
      key: 'danger',
      label: (
        <span style={{ color: '#ff4d4f' }}>
          <WarningOutlined style={{ marginRight: 8 }} />
          Danger Zone
        </span>
      ),
      children: (
        <DangerZone
          onStopAllJobs={handleStopAllJobs}
          onResetSettings={handleResetSettings}
          onFactoryReset={handleFactoryReset}
          onKillChrome={handleKillChrome}
          onGetChromeCount={handleGetChromeCount}
        />
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Settings"
        breadcrumbs={[
          { label: 'Dashboard', path: '/' },
          { label: 'Settings' },
        ]}
      />

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={tabItems}
        tabPosition="left"
        style={{ minHeight: 500 }}
      />
    </div>
  );
}

export default SettingsPage;
